/**
 * Navigation bar — TuviON dark with amber accent.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  FileText,
  Menu as MenuIcon,
  Plus,
  X,
  Network,
  Route,
  Shield,
  Users,
  Settings as SettingsIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { TuvionWordmark } from '@/components/brand/TuvionLogo';
import { cn } from '@/lib/utils';
import { useAuthSession } from '@/state/authSession';
import { useCurrentEvent } from '@/state/currentEvent';
import { SignOutButton } from '@/components/RequireSession';

import CommandDrawerHost from './CommandDrawer';

/** Tells EventTab to open its create dialog on arrival. Without it the pill would land on
 *  the tab with the CURRENT event loaded, and Save would overwrite that event instead of
 *  creating one. Imported by EventTab so the two ends cannot drift apart. */
export const NEW_EVENT_PARAM = 'new';

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Per-tab badge count. Roster carries the global-pending tally. */
  badge?: number;
};

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Read events list + current selection from the global context.
  // Pending-count badge for the Roster pill sums pending across all
  // events the operator can see; the context handles polling so we
  // don't double-fetch here.
  const { events, currentEvent, currentEventId, setCurrentEventId } = useCurrentEvent();
  const { principal } = useAuthSession();
  const pendingTotal = events.reduce(
    (acc, e) => acc + (e.enrollment_counts?.pending ?? 0),
    0,
  );

  // Tabs: Event · Fleet · Stages · Roster · (Team) · Settings. Legacy
  // /map, /monitoring + /logs URLs soft-redirect to /fleet (see
  // App.tsx) — the all-fleet map tab was removed: the event-scoped
  // public viewer is the real map surface.
  // Settings holds the global capture-quality (per-tier resolution /
  // bitrate / fps) + default upload-policy editor (RecordingSettingsCard);
  // surfaced as a tab so operators can find the quality settings.
  // Team is superadmin-only in the nav — server-side require_authz is
  // the REAL gate (see TeamPage.tsx's docstring), this just keeps the
  // tab bar honest for everyone else.
  const navItems: NavItem[] = [
    { label: 'Event', path: '/event', icon: <FileText /> },
    { label: 'Fleet', path: '/fleet', icon: <Network /> },
    { label: 'Stages', path: '/stages', icon: <Route /> },
    {
      label: 'Roster',
      path: '/roster',
      icon: <Users />,
      badge: pendingTotal > 0 ? pendingTotal : undefined,
    },
    ...(principal?.is_superadmin ? [{ label: 'Team', path: '/team', icon: <Shield /> }] : []),
    { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <>
      <header className="border-b border-border bg-gradient-to-br from-bg-surface to-bg-base shadow-card">
        <div className="flex h-14 items-center px-4">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 text-accent hover:bg-accent/10 md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </Button>

          {/* Logo + "Admin" — nowrap: the title broke to two lines at ≤1440
              once the nav filled out. */}
          <div className="mr-0 flex items-center gap-2.5 whitespace-nowrap md:mr-4">
            <TuvionWordmark className="h-5 md:h-6" />
            <span className="hidden border-l border-border-mid pl-2.5 text-sm font-medium text-fg-secondary md:inline">
              Admin
            </span>
          </div>

          {/* Global event selector pill (per design-system/ui_kits/mvp/admin.html:204).
              Replaces the per-tab Select dropdowns; Event / Stages /
              Roster all subscribe to the current selection via the
              CurrentEventProvider context. */}
          <div className="hidden md:mr-6 md:block">
            <EventSelectorPill
              events={events}
              currentEvent={currentEvent}
              currentEventId={currentEventId}
              onPick={(id) => setCurrentEventId(id)}
              onNewEvent={() => navigate(`/event?${NEW_EVENT_PARAM}=1`)}
            />
          </div>

          {/* Desktop navigation */}
          <nav className="hidden flex-1 ml-2 md:flex">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'flex items-center gap-2 border-b-[3px] border-transparent px-5 py-3 text-[15px] font-medium transition-colors duration-fast',
                    active
                      ? 'border-accent bg-bg-surface text-accent'
                      : 'text-fg-secondary hover:bg-bg-surface hover:text-fg',
                  )}
                >
                  <span className="[&_svg]:size-5">{item.icon}</span>
                  {item.label}
                  {item.badge != null && (
                    <span className="ml-1 rounded-full bg-status-warning px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-bg-base">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Command drawer trigger */}
          <CommandDrawerHost />

          {/* Status pill */}
          <div className="ml-2 hidden items-center gap-2 rounded-pill bg-bg-surface px-3 py-1 sm:flex">
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-online opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-online shadow-[0_0_10px_rgba(52,211,153,0.6)]" />
            </span>
            <span className="text-xs text-fg-secondary">online</span>
          </div>

          {/* Admin sign-out (renders nothing without a real session) */}
          <div className="ml-2 hidden sm:block">
            <SignOutButton />
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex h-full w-72 flex-col border-r border-border bg-bg-elevated">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-base font-semibold text-accent">Menu</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
              >
                <X />
              </Button>
            </div>
            <nav className="flex-1 py-2">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={cn(
                      'flex w-full items-center gap-3 border-l-[3px] border-transparent px-4 py-3 text-left text-sm font-medium transition-colors duration-fast',
                      active
                        ? 'border-accent bg-bg-surface text-accent'
                        : 'text-fg hover:bg-bg-surface',
                    )}
                  >
                    <span className={cn('[&_svg]:size-5', active && 'text-accent')}>{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.badge != null && (
                      <span className="rounded-full bg-status-warning px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-bg-base">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>
        </>
      )}
    </>
  );
};

export default Navbar;

// ────────────────────────────────────────────────────────────────────────
// EventSelectorPill — the brand-strip pill from admin.html:204
// ────────────────────────────────────────────────────────────────────────

interface EventSelectorPillProps {
  events: { id: number; name: string; active_from: string; active_until: string | null }[];
  currentEvent: { id: number; name: string } | null;
  currentEventId: number | null;
  onPick: (id: number) => void;
  onNewEvent: () => void;
}

const EventSelectorPill: React.FC<EventSelectorPillProps> = ({
  events,
  currentEvent,
  currentEventId,
  onPick,
  onNewEvent,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click-outside dismiss. Keydown ESC also closes.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = currentEvent?.name ?? 'No event selected';

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full border border-border bg-bg-surface px-3 py-1.5 text-sm font-medium text-fg hover:bg-bg-elevated"
      >
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-error opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-status-error" />
        </span>
        <span className="max-w-[240px] truncate">{label}</span>
        <ChevronDown className="size-3.5 text-fg-secondary" />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-1 w-[280px] overflow-hidden rounded-md border border-border bg-bg-elevated shadow-xl">
          <div className="max-h-[40vh] overflow-y-auto py-1">
            {events.length === 0 ? (
              <div className="px-3 py-2 text-xs text-fg-secondary">
                No events yet.
              </div>
            ) : (
              events.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => {
                    onPick(e.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-surface',
                    e.id === currentEventId && 'bg-bg-surface text-accent',
                  )}
                >
                  <span className="flex-1 truncate">{e.name}</span>
                  {e.id === currentEventId && (
                    <span className="text-[10px] uppercase tracking-wider text-accent">
                      current
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNewEvent();
            }}
            className="flex w-full items-center gap-2 border-t border-border bg-bg-surface px-3 py-2 text-sm font-medium text-accent hover:bg-bg-input"
          >
            <Plus className="size-3.5" />
            New event
          </button>
        </div>
      )}
    </div>
  );
};
